/**
 * A Team's limits, as the settings sheet drafts them: the ONE budget a ticket
 * gets (decision 6, 2026-10-19: planning, the build and its fix rounds share a
 * dollar ceiling and a time ceiling), plus how many turns the Team starts on
 * its own in a day.
 *
 * The sheet holds what a person typed (dollars, hours, turns) and Save turns
 * it into the units the charter stores: money in millicents ($1 = 100,000),
 * time in milliseconds. Only the fields a person changed go out. "Reset to
 * defaults" sends `ticketBudget: null` and `dailyTurnBudget: null`, which clear
 * the Team's own values so today's defaults apply (a Team whose old run limits
 * were larger keeps those, mapped: the server never gives a Team less).
 *
 * The old per-stage limits (research and development budgets, clocks and
 * model calls) are not settings any more; the model-call count and the
 * per-attempt clocks are internal safety constants.
 *
 * `runLimitsCharter` is the one validation gate for Save: `saveTeamSettings`
 * calls it before writing anything and throws `run_limits_invalid` on a bad
 * value. The card's per-field notes use `fieldProblem`, the same check.
 */
import {
  DEFAULT_DAILY_TURN_BUDGET,
  DEFAULT_TICKET_BUDGET,
  MAX_DAILY_TURN_BUDGET,
  MAX_TICKET_BUDGET_ELAPSED_MS,
  MAX_TICKET_BUDGET_MILLICENTS,
  MIN_TICKET_BUDGET_ELAPSED_MS,
  type TicketBudget,
} from "@yagni/shared";

const MILLICENTS_PER_DOLLAR = 100_000;
const MS_PER_HOUR = 3_600_000;

export type LimitField = "dollars" | "hours" | "turns";

export interface RunLimitsDraft {
  dollars: string;
  hours: string;
  turns: string;
  /** "Reset to defaults" was pressed and nothing was typed since. */
  reset: boolean;
  /** What the Team holds, as numbers, for Reset to compare against the defaults. */
  stored: {
    budget: TicketBudget;
    /** `desks.daily_turn_budget`; null is the default. */
    turns: number | null;
  };
}

export function formatDollars(millicents: number): string {
  const dollars = millicents / MILLICENTS_PER_DOLLAR;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

function formatHours(ms: number): string {
  const hours = ms / MS_PER_HOUR;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

export const MIN_BUDGET_HOURS = MIN_TICKET_BUDGET_ELAPSED_MS / MS_PER_HOUR;
export const MAX_BUDGET_HOURS = MAX_TICKET_BUDGET_ELAPSED_MS / MS_PER_HOUR;
export const MAX_BUDGET_DOLLARS = MAX_TICKET_BUDGET_MILLICENTS / MILLICENTS_PER_DOLLAR;

/** The draft a Team's limits start as; absent values read as the defaults. */
export function runLimitsDraftOf(
  budget: TicketBudget | null | undefined,
  dailyTurnBudget: number | null | undefined,
): RunLimitsDraft {
  const resolved = budget ?? DEFAULT_TICKET_BUDGET;
  return {
    dollars: formatDollars(resolved.maxCostMillicents),
    hours: formatHours(resolved.maxElapsedMs),
    turns: String(dailyTurnBudget ?? DEFAULT_DAILY_TURN_BUDGET),
    reset: false,
    stored: {
      budget: { ...resolved },
      turns: dailyTurnBudget ?? null,
    },
  };
}

/**
 * Today's defaults, as a draft that will clear the Team's own values. Save
 * compares the sheet's initial draft's `stored` numbers, never this one's.
 */
export function defaultRunLimitsDraft(): RunLimitsDraft {
  return { ...runLimitsDraftOf(DEFAULT_TICKET_BUDGET, null), reset: true };
}

/** Why a typed value cannot be saved, in the words of the field; null when it can. */
export function fieldProblem(field: LimitField, text: string): string | null {
  const value = Number(text.trim());
  const blank = text.trim() === "";
  if (field === "dollars") {
    const millicents = Math.round(value * MILLICENTS_PER_DOLLAR);
    return blank || !Number.isFinite(value) || millicents < 1 || millicents > MAX_TICKET_BUDGET_MILLICENTS
      ? `A dollar amount above zero, at most $${MAX_BUDGET_DOLLARS.toLocaleString("en-US")}.`
      : null;
  }
  if (field === "hours") {
    return blank || !Number.isFinite(value) || value < MIN_BUDGET_HOURS || value > MAX_BUDGET_HOURS
      ? `A number of hours from ${MIN_BUDGET_HOURS} to ${MAX_BUDGET_HOURS}.`
      : null;
  }
  return blank || !Number.isInteger(value) || value < 1 || value > MAX_DAILY_TURN_BUDGET
    ? `A whole number from 1 to ${MAX_DAILY_TURN_BUDGET.toLocaleString("en-US")}.`
    : null;
}

export interface RunLimitsCharter {
  ticketBudget?: { maxCostMillicents?: number | null; maxElapsedMs?: number | null } | null;
  dailyTurnBudget?: number | null;
}

function sameBudget(a: TicketBudget, b: TicketBudget): boolean {
  return a.maxCostMillicents === b.maxCostMillicents && a.maxElapsedMs === b.maxElapsedMs;
}

/**
 * What the charter PATCH carries for these drafts: only what changed, or an
 * error naming the first value that cannot be saved. Empty when nothing did.
 */
export function runLimitsCharter(
  initial: RunLimitsDraft,
  next: RunLimitsDraft,
): { ok: true; charter: RunLimitsCharter } | { ok: false; error: string } {
  if (next.reset) {
    const charter: RunLimitsCharter = {};
    if (!sameBudget(initial.stored.budget, DEFAULT_TICKET_BUDGET)) charter.ticketBudget = null;
    if (initial.stored.turns !== null) charter.dailyTurnBudget = null;
    return { ok: true, charter };
  }
  const budget: NonNullable<RunLimitsCharter["ticketBudget"]> = {};
  if (next.dollars.trim() !== initial.dollars) {
    const problem = fieldProblem("dollars", next.dollars);
    if (problem) return { ok: false, error: mustBe("The ticket budget", problem) };
    budget.maxCostMillicents = Math.round(Number(next.dollars.trim()) * MILLICENTS_PER_DOLLAR);
  }
  if (next.hours.trim() !== initial.hours) {
    const problem = fieldProblem("hours", next.hours);
    if (problem) return { ok: false, error: mustBe("The ticket's time", problem) };
    budget.maxElapsedMs = Math.round(Number(next.hours.trim()) * MS_PER_HOUR);
  }
  const charter: RunLimitsCharter = {};
  if (Object.keys(budget).length > 0) charter.ticketBudget = budget;
  if (next.turns.trim() !== initial.turns) {
    const problem = fieldProblem("turns", next.turns);
    if (problem) return { ok: false, error: mustBe("Automatic turns per day", problem) };
    charter.dailyTurnBudget = Number(next.turns.trim());
  }
  return { ok: true, charter };
}

/** "The ticket budget" + "A dollar amount…" → "The ticket budget must be a dollar amount…". */
function mustBe(subject: string, problem: string): string {
  return `${subject} must be ${problem.charAt(0).toLowerCase()}${problem.slice(1)}`;
}
